import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import DiscordLoginButton from '@/components/Auth/DiscordLoginButton'
import { useNotification } from '@/hooks/useNotification'
import { login as authLogin, ApiError } from '@/services/authService'
import '@/assets/login.css'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()
  const { showNotification } = useNotification()
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const pending = useRef(false)
  const errorRef = useRef<HTMLParagraphElement>(null)
  const requestedPath: unknown = location.state?.from
  const from =
    typeof requestedPath === 'string' &&
    requestedPath.startsWith('/') &&
    !requestedPath.startsWith('//')
      ? requestedPath
      : '/'

  useEffect(() => {
    if (error) errorRef.current?.focus()
  }, [error])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (pending.current) return
    const data = new FormData(event.currentTarget)
    const usernameOrEmail = String(data.get('usernameOrEmail') ?? '').trim()
    const password = String(data.get('password') ?? '')
    if (!usernameOrEmail || !password) {
      setError('Vyplň přihlašovací jméno nebo e-mail a heslo.')
      return
    }
    pending.current = true
    setIsSubmitting(true)
    setError('')
    try {
      const { user } = await authLogin({ usernameOrEmail, password })
      login(user)
      showNotification('success', 'Jsi přihlášený.', 'Vítej zpátky na Allcomu.')
      await navigate(from, { replace: true })
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.status === 401
          ? 'Přihlášení se nezdařilo. Zkontroluj jméno nebo e-mail a heslo.'
          : 'Přihlášení se teď nepodařilo. Zkus to za chvíli znovu.',
      )
    } finally {
      pending.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <div className="ac-signin ac-width">
      <div className="ac-signin-intro">
        <h1>
          Vítej <br />
          <span>zpátky.</span>
        </h1>
        <p>Tvůj profil, komunitní mince a nákupy. Všechno pod jedním účtem.</p>
      </div>

      <section className="ac-signin-panel" aria-labelledby="signin-title">
        <h2 id="signin-title">Přihlásit se</h2>
        <p>
          {from === '/shop'
            ? 'Po přihlášení se vrátíš do obchodu.'
            : 'Pokračuj přes Discord nebo svým účtem Allcom.'}
        </p>
        <DiscordLoginButton className="ac-signin-discord" disabled={isSubmitting} />
        <div className="ac-signin-divider">
          <span>nebo účtem Allcom</span>
        </div>

        <form
          onSubmit={(event) => {
            void handleSubmit(event)
          }}
          className="ac-signin-form"
          aria-busy={isSubmitting}
        >
          <div>
            <label htmlFor="signin-identity">Uživatelské jméno nebo e-mail</label>
            <input
              id="signin-identity"
              name="usernameOrEmail"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label htmlFor="signin-password">Heslo</label>
            <div className="ac-signin-password">
              <input
                id="signin-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                disabled={isSubmitting}
                aria-label={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
                aria-pressed={showPassword}
                aria-controls="signin-password"
              >
                {showPassword ? 'Skrýt' : 'Zobrazit'}
              </button>
            </div>
          </div>
          {error && (
            <p className="ac-signin-error" role="alert" tabIndex={-1} ref={errorRef}>
              {error}
            </p>
          )}
          <button type="submit" className="ac-signin-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Přihlašujeme…' : 'Přihlásit se účtem'}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <path d="M5 12h14m-6-6 6 6-6 6" />
            </svg>
          </button>
          {isSubmitting && (
            <p role="status" className="ac-signin-progress">
              Ověřujeme přihlašovací údaje…
            </p>
          )}
        </form>

        <p className="ac-signin-register">
          Ještě nemáš účet?{' '}
          <Link to="/register" className="ac-text-link">
            Zaregistrovat se
          </Link>
        </p>
        <p className="ac-signin-help">
          Potřebuješ s přihlášením pomoct?{' '}
          <a
            href="https://discord.allcom.zone"
            className="ac-text-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Napiš nám na Discordu.
          </a>
        </p>
      </section>
      <div className="ac-signin-community">
        <p>Jdeš si jen popovídat?</p>
        <a
          href="https://discord.allcom.zone"
          className="ac-text-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Přejít na Discord
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <path d="M5 12h14m-6-6 6 6-6 6" />
          </svg>
        </a>
      </div>
    </div>
  )
}
